import {
	ClientInformationRequest,
	InformationRequest,
	InformationResponsePayload,
	ViewType,
} from '../components/common/types';
import { shortRequestURISegment, shortResponseURISegment } from '../config/constants';
import ApiClient, { getSseClient } from './apiClient';
import {
	CreateRequestPayload,
	DataProvider,
	DuplicateRequestPayload,
	SetAssigneePayload,
	UpdateMetaRequestPayload,
	UpdateSchemaRequestPayload,
} from './types';

/**
 * Get information requests
 * GET /information-requests
 * params: //TODO
 * return: // array of information requests
 */
const getInformationRequests = async (
	containerId: string,
	view: ViewType = 'mini',
	isDeleted = false,
	take?: number,
	skip?: number,
	withPermissions?: boolean
) => {
	try {
		const searchParams = new URLSearchParams();

		// Append view parameter if provided
		if (view) {
			searchParams.append('view', view);
		}

		// Append isDeleted parameter if provided
		if (isDeleted) {
			searchParams.append('isDeleted', isDeleted.toString());
		}

		if (take) {
			searchParams.append('take', take.toString());
		}

		if (skip) {
			searchParams.append('skip', skip.toString());
		}

		// Convert searchParams to string and append it to the base URL
		const queryString = searchParams.toString();
		const url = `/${shortRequestURISegment}/${containerId}/list${
			queryString ? `?${queryString}` : ''
		}`;

		const { data } = await ApiClient.get(url);
		if (withPermissions) {
			return data;
		}
		return data.items;
	} catch (error) {
		throw new Error(`Error occurred in fetching information request list: ${error}`);
	}
};

const getInformationRequestsByUser = async (
	userRID: string,
	containerRID?: string,
	skip?: number,
	limit?: number
) => {
	try {
		const searchParams = new URLSearchParams();

		searchParams.append('assignedUserRID', userRID);

		if (containerRID) {
			searchParams.append('containerRID', containerRID);
		}

		if (skip) {
			searchParams.append('skip', skip.toString());
		}

		if (limit) {
			searchParams.append('limit', limit.toString());
		}

		const queryString = searchParams.toString();
		const url = `/ecosystem/tasks${queryString ? `?${queryString}` : ''}`;

		const { data } = await ApiClient.get(url);

		return data as ClientInformationRequest[];
	} catch (error) {
		throw new Error(
			`Error occurred in requesting information requests by user: ${error}`
		);
	}
};

/**
 * Get information request by information request id
 * GET /information-requests/{IR_RID}
 * params: //TODO
 * return: // an information request
 */
const getInformationRequest = async (informationRequestRID: string) => {
	try {
		const { data } = await ApiClient.get<InformationRequest>(
			`/${shortRequestURISegment}/${informationRequestRID}`
		);

		return data;
	} catch (error) {
		throw new Error(`Error getting information request: ${error}`);
	}
};

/**
 * Create
 * POST /information-requests
 * body: //TODO {}
 * return: // RID of information request
 */
const createInformationRequest = async (payload: CreateRequestPayload) => {
	try {
		const informationRequest = await ApiClient.post(
			`/${shortRequestURISegment}/`,
			payload
		);

		return informationRequest;
	} catch (error) {
		throw new Error(`Error creating information request: ${error}`);
	}
};
/**
 * PUT /information-requests/{IR_RID}/schema
 * body: //TODO {}
 * return: // array of information requests
 */

const updateInformationRequestSchema = async (
	informationRequestRID: string,
	payload: UpdateSchemaRequestPayload
) => {
	try {
		const informationRequest = await ApiClient.put(
			`/${shortRequestURISegment}/${informationRequestRID}/schema`,
			payload
		);

		return informationRequest;
	} catch (error) {
		throw new Error(`Error updating information request: ${error}`);
	}
};

/**
 * Update meta data of a information request
 * PUT /information-response/{IR_RID}/meta
 * body: {name, description, duedate, optional thankYouMessage }
 * return: // success message
 */
const updateInformationRequestMeta = async (
	informationRequestRID: string,
	payload: UpdateMetaRequestPayload
) => {
	try {
		const informationRequest = await ApiClient.put(
			`/${shortRequestURISegment}/${informationRequestRID}/meta`,
			payload
		);

		return informationRequest;
	} catch (error) {
		throw new Error(`Error updating information request meta: ${error}`);
	}
};

/**
 * Assign an assignee to an information request
 * PUT /information-request/{informationRequestRID}/assignee
 * body: //TODO {}
 * return: // array of information requests
 */
const setAssigneeToInformationRequest = async (
	informationRequestRID: string,
	payload: SetAssigneePayload
) => {
	try {
		const response = await ApiClient.put(
			`/${shortRequestURISegment}/${informationRequestRID}/assignee`,
			payload
		);

		return response;
	} catch (error) {
		throw new Error(`Error setting assignees to information request: ${error}`);
	}
};

// Information Responses

/**
 * Get information requests
 * GET /information-requests
 * params: //TODO
 * return: // array of information requests
 */
const getInformationResponses = async (containerId: string) => {
	try {
		const { data } = await ApiClient.get(
			`/${shortResponseURISegment}/${containerId}/list`
		);
		return data.responseList;
	} catch (error) {
		throw new Error(`Error occurred in fetching information response list: ${error}`);
	}
};

/**
 * Get information response for one user for one information request
 * GET /information-response/{informationRequestRID}/response
 * body: //TODO
 * return: // information response
 */
const getInformationResponse = async (informationRequestRID: string) => {
	try {
		const { data } = await ApiClient.get(
			`/${shortResponseURISegment}/${informationRequestRID}/response`
		);
		return data;
	} catch (error) {
		throw new Error(`Error occurred in fetching single information response: ${error}`);
	}
};

/**
 * Gets an information-response for a given RID
 * GET /information-response/{informationResponseRID}
 * return: // information response
 */
const getInformationResponseByResponseRID = async (informationResponseRID: string) => {
	try {
		const { data } = await ApiClient.get(
			`/${shortResponseURISegment}/${informationResponseRID}`
		);
		return data;
	} catch (error) {
		throw new Error(
			`Error occurred in fetching information response using informationResponseRID: ${error}`
		);
	}
};

/**
 * Create an information response
 * POST /information-response/
 * body: //TODO {}
 * return: // RID of information response
 */
const createInformationResponse = async (
	payload: InformationResponsePayload,
	signal?: AbortSignal
) => {
	try {
		const informationResponse = await ApiClient.post(
			`/${shortResponseURISegment}/`,
			payload,
			{
				signal,
			}
		);
		return informationResponse;
	} catch (error) {
		throw new Error(`Error creating information response: ${error}`);
	}
};

/**
 * Remove an assignee from an information request
 * DELETE /information-request/{informationRequestRID}/assignee/{assigneeID}
 * return: // success/failure for API call
 */
const deleteAssigneeFromInformationRequest = async (
	informationRequestRID: string,
	assigneeId: string
) => {
	try {
		const response = await ApiClient.delete(
			`/${shortRequestURISegment}/${informationRequestRID}/assignee/${assigneeId}`
		);

		return response;
	} catch (error) {
		throw new Error(`Error deleting assignee from information request: ${error}`);
	}
};

/**
 * Deletes an information request for given RID
 * DELETE /information-requests/{IR_RID}
 * return: // a string
 */
const deleteInformationRequest = async (informationRequestRID: string) => {
	try {
		const informationRequest = await ApiClient.delete(
			`/${shortRequestURISegment}/${informationRequestRID}`
		);
		return informationRequest;
	} catch (error) {
		throw new Error(`Error deleting information request: ${error}`);
	}
};

/**
 * Closes/Disables an information-request for a given RID
 * PUT /information-requests/{IR_RID}/close
 * return: // a string
 */
const closeInformationRequest = async (
	informationRequestRID: string,
	closureMessage?: string
) => {
	try {
		const requestBody = closureMessage ? { closureMessage } : {};
		const informationRequest = await ApiClient.put(
			`/${shortRequestURISegment}/${informationRequestRID}/close`,
			requestBody
		);
		return informationRequest;
	} catch (error) {
		throw new Error(`Error closing information request: ${error}`);
	}
};

/**
 * Duplicate
 * POST /information-requests/{IR_RID}/clone
 * return: // RID of information request
 */
const duplicateInformationRequest = async (
	informationRequestRID: string,
	payload: DuplicateRequestPayload
) => {
	try {
		const informationRequest = await ApiClient.post(
			`/${shortRequestURISegment}/${informationRequestRID}/clone`,
			payload
		);
		return informationRequest;
	} catch (error) {
		throw new Error(`Error duplicating information request: ${error}`);
	}
};

/**
 * Restores an information request for given RID
 * PUT /information-request/{IR_RID}/restore
 * return: // a string
 */
const restoreInformationRequest = async (informationRequestRID: string) => {
	try {
		const informationRequest = await ApiClient.put(
			`/${shortRequestURISegment}/${informationRequestRID}/restore`
		);
		return informationRequest;
	} catch (error) {
		throw new Error(`Error restoring information request: ${error}`);
	}
};

/**
 * Sends email reminder for all assignees of an information-request for a given RID
 * POST /information-requests/{IR_RID}/reminder
 * return: // a string
 */
const remindAssigneesInformationRequest = async (
	informationRequestRID: string,
	reminderMessage?: string
) => {
	try {
		const requestBody = reminderMessage ? { reminderMessage } : {};
		const informationRequest = await ApiClient.post(
			`/${shortRequestURISegment}/${informationRequestRID}/reminder`,
			requestBody
		);
		return informationRequest;
	} catch (error) {
		throw new Error(`Error sending reminders for information request: ${error}`);
	}
};

/**
 * Fetch responses for the searched query
 * GET /information-response/{informationRequestRID}/response/search?q=keyword
 * return: list of related search results
 */

const getConnectedEntityData = async (
	connectedEntityRID: string,
	searchTerm: string,
	abortController?: AbortController
) => {
	try {
		if (searchTerm !== '') {
			const { data } = await ApiClient.get(
				`/${shortResponseURISegment}/${connectedEntityRID}/response/search?q=${searchTerm}`,
				{
					signal: abortController?.signal,
				}
			);
			return data;
		}
	} catch (error) {
		throw new Error(
			`Error occurred in fetching responses for the searched query: ${error}`
		);
	}
};

/**
 * Get information responses as CSV
 * GET /inforeq/{IR_RID}/export
 * return: CSV file
 */
const exportInformationResponses = async (informationRequestRID: string) => {
	try {
		const response = await ApiClient.get(
			`/${shortRequestURISegment}/${informationRequestRID}/export`,
			{
				responseType: 'blob',
			}
		);

		const contentDisposition = response.headers['content-disposition'];
		const filenameMatch = contentDisposition.split('filename=');
		const filename = filenameMatch[1] || 'download.csv'; // Fallback filename
		const textData = await response.data.text();
		const rowCount = textData.split('\n').filter((row: string) => row.trim()).length - 1;
		const fileResponse = {
			filename,
			rowCount,
			content: response.data,
		};

		return fileResponse;
	} catch (error) {
		throw new Error(`Error exporting information responses: ${error}`);
	}
};

/**
 * Creates form schema using AI based on form description
 * SSE /ai/generate-form (Server-Sent Events endpoint)
 * body: { form_description: string }
 * return: Generated form schema (parsed from streaming response)
 */
const createInformationRequestWithAI = async (
	body: { form_description: string },
	onStatusUpdate?: (status: string, message: string, jobId?: string) => void
) => {
	if (!body.form_description?.trim()) {
		throw new Error('Form description is required for AI generation');
	}

	try {
		const params = new URLSearchParams();

		const form_descriptionWrapper = `Generate a JSON schema for a dynamic form based on the following description: "${body.form_description}". Always leave the validations array empty - do not add any validation rules.`;

		params.append('form_description', JSON.stringify(form_descriptionWrapper));

		const sseClient = getSseClient(`/ai/generate-form?${params.toString()}`);
		let timeoutId: NodeJS.Timeout;

		return new Promise((resolve, reject) => {
			const cleanup = () => {
				sseClient.close();
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			};

			const handleError = (message: string) => {
				cleanup();
				onStatusUpdate?.('error', message);
				reject(new Error(message));
			};

			sseClient.onopen = () => {
				onStatusUpdate?.('connecting', 'Connected to AI generation service');
			};

			sseClient.onmessage = event => {
				try {
					const data = JSON.parse(event.data);

					switch (data.status) {
						case 'submitted':
							onStatusUpdate?.(
								'submitted',
								data.result?.message || 'Request submitted',
								data.result?.job_id
							);
							break;

						case 'connecting':
							onStatusUpdate?.(
								'connecting',
								data.result?.message || 'Connecting to AI service'
							);
							break;

						case 'processing':
							onStatusUpdate?.(
								'processing',
								data.result?.message || 'Processing request'
							);
							break;

						case 'success':
							cleanup();
							try {
								if (!data.result?.generated_form) {
									handleError('No generated form found in response');
									return;
								}

								const finalSchema = JSON.parse(data.result.generated_form);

								if (!finalSchema.fields || !finalSchema.layout) {
									handleError('Generated schema missing required fields or layout');
									return;
								}

								onStatusUpdate?.('success', 'Form generation completed successfully');
								resolve(finalSchema);
							} catch (parseError) {
								handleError(`Failed to parse generated schema: ${parseError}`);
							}
							break;

						case 'error':
						case 'failed':
							const errorMessage =
								data.result?.message || data.result?.error || 'AI generation failed';
							handleError(errorMessage);
							break;

						default:
							console.warn(`Unknown SSE status received: ${data.status}`);
							break;
					}
				} catch (parseError) {
					console.error('Failed to parse SSE event data:', parseError);
					handleError('Invalid response format from AI service');
				}
			};

			sseClient.onerror = error => {
				console.error('SSE connection error:', error);
				handleError('Failed to connect to AI generation service');
			};

			// 2 minute timeout
			timeoutId = setTimeout(() => {
				handleError('AI generation timeout after 2 minutes');
			}, 120000);
		});
	} catch (error) {
		throw new Error(`Error creating information request with AI: ${error}`);
	}
};

export const defaultDataProvider: DataProvider = {
	getInformationRequests,
	getInformationRequestsByUser,
	getInformationRequest,
	createInformationRequest,
	updateInformationRequestSchema,
	updateInformationRequestMeta,
	setAssigneeToInformationRequest,
	getInformationResponses,
	getInformationResponse,
	getInformationResponseByResponseRID,
	createInformationResponse,
	deleteAssigneeFromInformationRequest,
	deleteInformationRequest,
	closeInformationRequest,
	duplicateInformationRequest,
	restoreInformationRequest,
	remindAssigneesInformationRequest,
	getConnectedEntityData,
	exportInformationResponses,
	createInformationRequestWithAI,
};
